import { apiSendContactForm } from "../api/contact/query/post";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  ThemeProvider,
  CssBaseline,
} from "@mui/material";
import { theme } from "../mui.config"

import ContactBanner from '../assets/dcnc_early_access2.png';

const RegisterInterestSchema = z.object({
  name: z.string().min(1, "Name is required"),
  email: z.string().email("Invalid email address"),
  companyName: z.string() || "",
});

type RegisterInterestFormData = z.infer<typeof RegisterInterestSchema>;

export const ContactPage = () => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = useForm<RegisterInterestFormData>({
    resolver: zodResolver(RegisterInterestSchema),
    defaultValues: {
      name: '',
      email: '',
      companyName: '',
    },
  });

  const onSubmit = async (data: RegisterInterestFormData) => {
    try {
      const res = await apiSendContactForm(data);
      if (res?.status !== 200) {
        alert("Something went wrong. Please try again.");
      }
    } catch (err) {
      alert("Error sending message.");
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline /> {/* Apply global dark mode styles */}
      <Container  sx={{
          display: 'flex',
          flexDirection: 'column',
              alignSelf: 'start',
          justifyContent: {
              xs: 'start',
              sm: 'start',
              md: 'center'
          },
          maxWidth: {
              xs: '80vw',
              sm: '80vw',
              md: '60vw',
              xl: '50vw'
          },
          minHeight: '80vh',
          color: 'primary',


      }}>

                <Box
                    component="img"
                    src={ContactBanner}
                    sx={{
                        maxWidth: '100%',
                    }}
                    className="img-fluid mb-3 img-topper"
                    alt="DigitalCNC Logo"
                    width="804"
                    height="596"
                />

        <Typography variant="h3" align="left" color="primary.contrastText" gutterBottom
                    sx={{
                            fontSize: {
                                xs: '1.5rem', // on extra-small screens
                                sm: '2rem',   // on small screens
                                md: '2.5rem', // on medium screens
                            }
                    }}
        >
          A precision manufacturing software company using AI to simulate real machine behaviour — enabling faster, smarter, and more accurate CNC operations.
        </Typography>

        <Typography variant="body2" align="left" color="primary.contrastText" gutterBottom>
          Founded by Dr Rob Ward in partnership with the University of Sheffield AMRC and Yorkshire AI Labs, DigitalCNC blends world-class
          research in control systems with commercial expertise from Silicon Valley veterans. Backed by aerospace leaders and years of
          validation, DigitalCNC represents a new frontier in advanced manufacturing — helping industry reduce waste, improve accuracy,
          and boost productivity at scale.

        </Typography>
        <Typography variant="h4" align="left" gutterBottom sx={{
            fontSize: '1.1em',
            padding: '40px 0px 0px 0px',
            color: 'primary.contrastText',
            fontWeight: 'fontWeightHeavy'
        }}>
          Register Your Interest
        </Typography>
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            gap: '5px',
            width: '100%',
            maxWidth: '100%',
          }}
        >
          {/* Name Field */}
          <TextField
            fullWidth={false}
            placeholder="Name"
            margin="none"
            {...register("name")}
            error={!!errors.name}
            helperText={errors.name?.message}
            variant="outlined"
            className="form-control"
          />
          {/* Email Field */}
          <TextField
            fullWidth={false}
            placeholder="Email"
            type="email"
            margin="none"
            {...register("email")}
            error={!!errors.email}
            helperText={errors.email?.message}
            variant="outlined"
            className="form-control"
          />
          {/* Company Name Field */}
          <TextField
            fullWidth={false}
            placeholder="Company Name"
            margin="none"
            {...register("companyName")}
            error={!!errors.companyName}
            helperText={errors.companyName?.message}
            variant="outlined"
            className="form-control"
          />
          {/* Submit Button */}
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            sx={{
              textAlign: 'left',
              backgroundColor: 'primary.contrastText',
              color: 'primary.main',
              mt: 2,
              padding: '15px',
              fontSize: '1.1em',
              fontWeight: 'bold',
              borderRadius: '10px',
              textTransform: 'none',
              flexBasis: { xs: '100%', sm: 'calc(52.5% - 2px)' },
            }}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Sending..." : "Register"}
          </Button>

          {/* Success Message */}
          {isSubmitSuccessful && (
            <Typography color="success.main" align="left" mt={2} sx={{ color: '#90ee90' }}>
              Your message has been sent!
            </Typography>
          )}
        </Box>
      </Container>
    </ThemeProvider>
  );
};
